/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  Grid,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Space,
  Text,
  TextLabel,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InputChildRef } from '../../../../../components/Common/Form/InputWithValid';
import { TextareaWithValid } from '../../../../../components/Common/Form/TextareaWithValid';
import config from '../../../../../config';
import { ValidProps } from '../../../../../types';
import {
  RecipientGroupsProps,
  RecipientGroupType,
} from '../../../../../types/globalSetting';
import common from '../../../../../utils/common';
import { reportPendo } from '../../../../../utils/setupPendo';
import { validateIsNotEmptyString } from '../../../../../utils/validate';
import { onUpdateRecipientGroup } from '../shared';

export const DefaultGroupModal = React.memo(
  (props: {
    onClose: () => void;
    visible: boolean;
    loadAllRecipientGroups: () => void;
    data?: RecipientGroupsProps | undefined;
    withEditPermission: boolean;
  }) => {
    const { t } = useTranslation();
    const [recipients, setRecipients] = useState('');
    useEffect(() => {
      setRecipients(
        props.data && props.data.emails.length > 0
          ? props.data?.emails.join(',')
          : ''
      );
    }, [props.data?.emails]);

    const onUpdate = async () => {
      await textareaChildRef.current?.validate(recipients);
      if (textareaChildRef.current?.errorMsg !== undefined) return;
      props.onClose();
      await onUpdateRecipientGroup((props.data as any).id, recipients, t);
      await props.loadAllRecipientGroups();
      reportPendo('GlobalSetting.RecipientGroup.UpdateRecipientGroup', {
        id: (props.data as any).id,
        type: (props.data as any).type,
        number: recipients.split(',').length,
      });
    };
    const [colorMode] = useColorMode();
    const [colorStyle] = useColorStyle({ colorMode });
    const textareaChildRef = useRef<InputChildRef>(null);
    const validatesRecipients: ValidProps[] = [
      {
        validMethod: (value: string) => {
          const emails = value.split(',');
          for (const email of emails) {
            if (!common.validateEmail(email)) {
              return false;
            }
          }
          return true;
        },
        errMsg: t(
          'globalSetting.recipientGroup.recipientTextarea.validate.invalidFormat'
        ),
      },
      {
        validMethod: validateIsNotEmptyString,
        errMsg: t(
          'globalSetting.recipientGroup.recipientTextarea.validate.empty'
        ),
      },
    ];
    return (
      <Modal
        closeOnEsc
        closeOnOutsideClick
        isClosable
        isOpen={props.visible && props.data?.isDefault}
        onClose={props.onClose}
        size="sm"
        p="0 8"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {props.data?.type === RecipientGroupType.POLICY
              ? t(
                  'globalSetting.recipientGroup.defaultGroup.policyNotification.name'
                )
              : t(
                  'globalSetting.recipientGroup.defaultGroup.systemNotification.name'
                )}
          </ModalHeader>
          <ModalBody>
            {/* <Text>
              {props.data?.type === RecipientGroupType.POLICY
                ? t('globalSetting.recipientGroup.defaultGroup.description')
                : t(
                    'globalSetting.recipientGroup.defaultGroup.systemNotification.description'
                  )}
            </Text> */}
            <Grid
              templateColumns="min-content auto"
              columnGap="3x"
              rowGap="3x"
              m="12px 0px"
            >
              <Grid>
                <TextLabel whiteSpace="nowrap">
                  {t(
                    'globalSetting.recipientGroup.defaultGroup.label.recipients.title'
                  )}
                  :<Space color="red:50">*</Space>
                </TextLabel>
              </Grid>
              <Grid>
                <TextareaWithValid
                  ref={textareaChildRef}
                  onChange={setRecipients}
                  value={recipients}
                  validates={validatesRecipients}
                  maxLength={
                    config.ui.globalSettings.limitation
                      .recipientGroupTextAreaMaxLength
                  }
                  placeholder={t(
                    'globalSetting.recipientGroup.defaultGroup.label.recipients.placeholder'
                  )}
                />
                <Text color={colorStyle.color.secondary} mt={4}>
                  {t('globalSetting.recipientGroup.defaultGroup.textArea.note')}
                </Text>
              </Grid>
            </Grid>
            {props.data?.type === RecipientGroupType.POLICY && (
              <Text>
                <Space fontWeight={700}>{t('globalSetting.note.title')}</Space>
                <Space width="1x"></Space>
                {t('globalSetting.recipientGroup.defaultGroup.notice')}
              </Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="primary"
              minWidth="20x"
              disabled={!props.withEditPermission}
              onClick={onUpdate}
            >
              {t('common.button.save')}
            </Button>
            <Space width="2x" />
            <Button minWidth="20x" onClick={props.onClose}>
              {t('common.button.cancel')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }
);
